import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useEmailApiClient } from '@/api/clients/useEmailApiClient.util.ts';
import {
  CreateDraftEmailResponseContentDto,
  EmailDraftDataDto,
  ResourceIdDto,
} from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';

type Req = {
  data: EmailDraftDataDto;
  resource?: ResourceIdDto;
  userId: string;
};

type Return = {
  createEmailDraft: (req: Req) => Promise<CreateDraftEmailResponseContentDto>;
  isCreatingEmailDraft: boolean;
};

export const useCreateEmailDraft = (): Return => {
  const { emailUpdateApi } = useEmailApiClient();
  const queryClient = useQueryClient();

  const { isPending, mutateAsync } = useMutation<
    CreateDraftEmailResponseContentDto,
    AxiosError,
    Req
  >({
    meta: { disableSuccessAlert: true },
    mutationFn: async req => {
      const res = await emailUpdateApi.createDraftEmail(
        req.userId,
        req.data,
        req.resource?.resourceId,
        req.resource?.type,
      );
      return res.data;
    },
    mutationKey: [QueryKeyEnum.EMAILS.DRAFT.CREATE],
    onSuccess: async res => {
      await queryClient.refetchQueries(
        { queryKey: [QueryKeyEnum.EMAILS.THREAD, res.emailThreadId] },
        { throwOnError: false },
      );
    },
  });

  return {
    createEmailDraft: mutateAsync,
    isCreatingEmailDraft: isPending,
  };
};
